import { t } from 'i18next'
import { FC, ReactNode, useState } from 'react'
import { Link } from 'react-router-dom'

import { Box, Button, Divider, Typography } from '@mui/material'

import { Screenshot } from '../../../../../api/core'
import { AnalystOverview } from '../../../../../components/AnalystOverview/AnalystOverview'
import { Features } from '../../../../../components/Features/Features'
import GalleryCarousel, { GalleryCarouselImageListingType } from '../../../../../components/GalleryCarousel/GalleryCarousel'
import { HtmlContentParser } from '../../../../../components/HtmlContentParser/HtmlContentParser'
import { liveEventReviewModalParserOptions } from '../../../../../components/LiveEventModalLink/LiveEventModalLink'
import { MaskedText } from '../../../../../components/MaskedText/MaskedText'
import { ReviewContent } from '../../../../../components/Review/Review'
import { getRandomIntBetween } from '../../../../../helpers/random'
import languageService from '../../../../../services/LanguageService'
import { Analyst } from '../../../../../types/Analyst'
import { LockedFeatureId } from '../../../../../types/LockedFeature'
import { useLiveEventsTrackerAccessCheck } from '../../../../account/hooks/roleHooks'
import { useCurrentUserLanguage } from '../../../../account/hooks/useCurrentUserLanguage'
import { Game } from '../../../../game'
import type { EventComment } from '../../../../live-events/types/TrackingEvents'
import { useIsGameOpenForEveryone } from '../../../../settings'
import { useNewsCardFeatures, useNewsCardScreenshots } from '../../../hooks/unifiedNewsEntryHooks'
import newsEntriesService from '../../../services/NewsEntriesService'
import { NewsEntryType, UnifiedNewsEntry } from '../../../types/UnifiedNewsEntry'
import { NewsCardContentAnalystContentSection } from './NewsCardContentAnalystContentSection/NewsCardContentAnalystContentSection'
import { NewsCardContentEditorialSection } from './NewsCardContentEditorialSection/NewsCardContentEditorialSection'
import { NewsCardContentFeaturesSection } from './NewsCardContentFeaturesSection/NewsCardContentFeaturesSection'
import { NewsCardContentReleaseNotesSection } from './NewsCardContentReleaseNotesSection/NewsCardContentReleaseNotesSection'
import { NewsCardContentReportSection } from './NewsCardContentReportSection/NewsCardContentReportSection'
import { NewsCardContentRevenueAndDownloadsSection } from './NewsCardContentRevenueAndDownloadsSection/NewsCardContentRevenueAndDownloadsSection'
import { NewsCardContentScreenshotsSection } from './NewsCardContentScreenshotsSection/NewsCardContentScreenshotsSection'
import { NewsCardContentSofLaunchMarketsSection } from './NewsCardContentSofLaunchMarketsSection/NewsCardContentSofLaunchMarketsSection'
import { NewsCardContentUpgradePlanSectionSection } from './NewsCardContentUpgradePlanSectionSection/NewsCardContentUpgradePlanSectionSection'

type NewsCardContentSectionsProps = {
  newsEntry: UnifiedNewsEntry
  hideGameDetails?: boolean
  hasNewsFeedLimitedAccess?: boolean
  limitedFunctionalityBannerType?: LockedFeatureId
  children?: ReactNode
  showLimitedContent?: boolean
}

export const NewsCardContentSections: FC<NewsCardContentSectionsProps> = ({
  newsEntry,
  hideGameDetails,
  hasNewsFeedLimitedAccess,
  limitedFunctionalityBannerType,
  children,
  showLimitedContent,
}) => {
  const currentUserLanguage = useCurrentUserLanguage()
  const entryType = newsEntry.feedEntry.type
  const isEditorialSection = entryType === NewsEntryType.Editorial
  const isReportSection = entryType === NewsEntryType.Report
  const softLaunchMarkets = newsEntriesService.getSoftLaunchMarketsFromNewsEntry(newsEntry.feedEntry)
  const game = { id: newsEntry.feedEntry.entryData.gameId } as Game
  const isGameOpenForEveryone = useIsGameOpenForEveryone(game)
  const [changedFeatures, updatedFeatures] = useNewsCardFeatures({ newsEntry })
  const { gameUpdateScreenshots, versionScreenshots, liveEventScreenshots } = useNewsCardScreenshots({ newsEntry: newsEntry })
  const [imageListingType, setImageListingType] = useState<GalleryCarouselImageListingType>('carousel')
  const fileNameVersion: string = newsEntry.feedEntry.entryData.version ? newsEntry.feedEntry.entryData.version : newsEntry.feedEntry.entryData.gameVersion

  return (
    <>
      {/* Editorial */}
      {isEditorialSection && <NewsCardContentEditorialSection newsEntry={newsEntry} />}

      {/* Report */}
      {isReportSection && <NewsCardContentReportSection newsEntry={newsEntry} />}

      {/* REVENUE AND DOWNLOADS */}
      {(newsEntry.dhistory || newsEntry.history || newsEntry.revenueDownloadsData) && (
        <NewsCardContentRevenueAndDownloadsSection newsEntry={newsEntry} entryType={entryType} />
      )}

      {/* ANALYST OVERVIEW */}
      {newsEntry.review && !newsEntry.comment && entryType !== NewsEntryType.GpsUpdate && newsEntry.review?.analyst && (
        <NewsCardContentAnalystContentSection
          analyst={newsEntry.review?.analyst}
          newsEntry={newsEntry}
          title={t('reviews:label_analyst_overview')}
          review={newsEntry.review?.gameReview}
          showLimitedContent={showLimitedContent}
        >
          <AnalystOverview review={newsEntry.review?.gameReview} language={currentUserLanguage} />
        </NewsCardContentAnalystContentSection>
      )}

      {/* ANALYST COMMENT */}
      {newsEntry.comment && entryType === NewsEntryType.GpsUpdate && newsEntry.comment?.analyst && (
        <NewsCardContentAnalystContentSection
          analyst={newsEntry.comment.analyst}
          newsEntry={newsEntry}
          title={t('reviews:label_analyst_comment')}
          rawContent={
            newsEntry.comment.analysisComment.content?.comment?.[currentUserLanguage] ||
            newsEntry.comment.analysisComment.content?.comment?.[languageService.defaultLanguage]
          }
          showLimitedContent={showLimitedContent}
        >
          <ReviewContent
            contentHtml={
              newsEntry.comment.analysisComment.content?.comment?.[currentUserLanguage] ||
              newsEntry.comment.analysisComment.content?.comment?.[languageService.defaultLanguage]
            }
          />
        </NewsCardContentAnalystContentSection>
      )}

      {/* LIVE EVENT DESCRIPTION or LIVE EVENT ANALYST NOTE*/}
      {(entryType === NewsEntryType.LiveEventNewEventType || entryType === NewsEntryType.LiveEventAnalystNotes) &&
        ((newsEntry.feedEntry.entryData?.comment && newsEntry.feedEntry.entryData.analyst) || showLimitedContent) && (
          <NewsCardContentAnalystContentSection
            analyst={newsEntry.feedEntry.entryData.analyst || ({} as Analyst)}
            newsEntry={newsEntry}
            title={t('common:description')}
            showLimitedContent={showLimitedContent}
            rawContent={
              newsEntry.feedEntry.entryData?.comment
                ? (newsEntry.feedEntry.entryData.comment as EventComment)?.content.comment?.[currentUserLanguage] ||
                  (newsEntry.feedEntry.entryData.comment as EventComment)?.content.comment?.[languageService.defaultLanguage]
                : undefined
            }
          >
            <LiveEventAnalystContent newsEntry={newsEntry} />
          </NewsCardContentAnalystContentSection>
        )}

      {/* LIVE EVENT TRACKED GAME */}
      {entryType === NewsEntryType.LiveEventTrackedGame && (
        <>
          <Divider sx={{ my: 2 }} />
          <Box display="flex" justifyContent="center" mb={2}>
            <Link to={`/live-events-tracker/games?gameIds=${game.id}`}>
              <Button variant="contained" color="primary" size="small">
                {t('overview:live_events_tracker_button')}
              </Button>
            </Link>
          </Box>
        </>
      )}

      {/* SOFT LAUNCH */}
      {softLaunchMarkets.length > 0 && <NewsCardContentSofLaunchMarketsSection softLaunchMarkets={softLaunchMarkets} />}

      {/* RELEASE NOTES */}
      {!hideGameDetails && newsEntry.versionInfo && <NewsCardContentReleaseNotesSection versionInfo={newsEntry.versionInfo} />}

      {/* CHANGED FEATURES */}
      {changedFeatures.length > 0 && (
        <NewsCardContentFeaturesSection title={t('newsfeed:changes_in_feature_set_topic')} locked={showLimitedContent}>
          <Features.Changed.List features={changedFeatures} />
        </NewsCardContentFeaturesSection>
      )}

      {/* UPDATED FEATURES */}
      {updatedFeatures.length > 0 && (
        <NewsCardContentFeaturesSection title={t('common:updated_features')} locked={showLimitedContent && !isGameOpenForEveryone}>
          <Features.Updated.List features={updatedFeatures} />
        </NewsCardContentFeaturesSection>
      )}

      {/* GAME UPDATE SCREENSHOTS */}
      {!hideGameDetails && gameUpdateScreenshots.length > 0 && (
        <NewsCardContentScreenshotsSection title={t('newsfeed:label_screenshots')}>
          <GalleryCarousel
            screenshots={gameUpdateScreenshots as Screenshot[]}
            imageListingType={imageListingType}
            hasNewsFeedLimitedAccess={showLimitedContent}
            onImageListingTypeChange={(newType) => setImageListingType(newType)}
            limitWidthForOneImage={true}
            filename={`game_update_screenshots-${newsEntry.feedEntry.entryData.game}-${fileNameVersion}`
              .toLocaleLowerCase()
              .trim()
              .replace(/[^a-zA-Z0-9_-]/g, '')}
            showPlayIcon={false}
          />
        </NewsCardContentScreenshotsSection>
      )}

      {/* GAME VERSION SCREENSHOTS */}
      {!hideGameDetails && versionScreenshots.length > 0 && (
        <NewsCardContentScreenshotsSection title={t('newsfeed:label_screenshots')}>
          <GalleryCarousel
            screenshots={versionScreenshots}
            imageListingType={imageListingType}
            hasNewsFeedLimitedAccess={showLimitedContent}
            onImageListingTypeChange={(newType) => setImageListingType(newType)}
            limitWidthForOneImage={true}
            filename={`game_version_screenshots-${newsEntry.feedEntry.entryData.game}-${fileNameVersion}`
              .toLocaleLowerCase()
              .trim()
              .replace(/[^a-zA-Z0-9_-]/g, '')}
            showPlayIcon={false}
          />
        </NewsCardContentScreenshotsSection>
      )}

      {/* LIVE EVENT SCREENSHOTS */}
      {liveEventScreenshots.length > 0 && (
        <NewsCardContentScreenshotsSection title={t('newsfeed:label_live_event_screenshots')}>
          <GalleryCarousel
            screenshots={liveEventScreenshots}
            imageListingType={imageListingType}
            hasNewsFeedLimitedAccess={showLimitedContent}
            liveEventModalLinkProps={{
              trackedGameId: newsEntry.feedEntry.entryData.gameId,
              eventId: newsEntry.feedEntry.entryData.eventId,
              eventTypeId: newsEntry.feedEntry.entryData.typeId,
            }}
            onImageListingTypeChange={(newType) => setImageListingType(newType)}
            limitWidthForOneImage={true}
            filename={`let-${newsEntry.feedEntry.entryData.game}_${languageService.getTranslation('tags', newsEntry.feedEntry.entryData.typeId)}`
              .toLocaleLowerCase()
              .trim()
              .replace(/ /g, '_') // Replaces spaces with underscores
              .replace(/[^a-zA-Z0-9_-]/g, '')} // Removes all other special characters except _ and -
          />
        </NewsCardContentScreenshotsSection>
      )}

      {/* LIMITED FUNCTIONALITY BANNER */}
      {limitedFunctionalityBannerType && (
        <NewsCardContentUpgradePlanSectionSection lockedFeatureId={limitedFunctionalityBannerType} showLimitedContent={showLimitedContent} />
      )}
    </>
  )
}

export type NewsCardContentSectionProps = {
  newsEntry: UnifiedNewsEntry
}

type LiveEventAnalystContentProps = {
  newsEntry: UnifiedNewsEntry
  rawHtml?: string
}
export const LiveEventAnalystContent: FC<LiveEventAnalystContentProps> = ({ newsEntry, rawHtml }) => {
  const hasAccessToLiveEventsTracker = useLiveEventsTrackerAccessCheck()
  const currentUserLanguage = useCurrentUserLanguage()

  const content =
    rawHtml ||
    (newsEntry.feedEntry.entryData.comment as EventComment)?.content.comment?.[currentUserLanguage] ||
    (newsEntry.feedEntry.entryData.comment as EventComment)?.content.comment?.[languageService.defaultLanguage] ||
    ''

  return hasAccessToLiveEventsTracker ? (
    <HtmlContentParser rawHtml={content} parserOptions={liveEventReviewModalParserOptions(newsEntry.feedEntry.gameId)} />
  ) : (
    <>
      <Typography variant="h3">
        <MaskedText length={getRandomIntBetween(45, 80)} />
      </Typography>
      <Typography variant="body1">
        <MaskedText length={getRandomIntBetween(90, 120)} />
        <MaskedText length={getRandomIntBetween(15, 45)} />
        <MaskedText length={getRandomIntBetween(50, 110)} />
      </Typography>
    </>
  )
}
